import React, { useState, useEffect } from 'react';
import { headToHeadManager } from '../services/api/managers';
import '../styles/index.css';

const HeadToHeadPage = ({ 
  leagueId, 
  homeTeamId, 
  awayTeamId, 
  homeTeamLogo, 
  awayTeamLogo, 
  homeTeamName, 
  awayTeamName,
  onH2hDataReceived 
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [team1, setTeam1] = useState(homeTeamId || 33); 
  const [team2, setTeam2] = useState(awayTeamId || 42); 
  const [selectedLeague, setSelectedLeague] = useState(leagueId || 39);

  useEffect(() => {
    if (team1 === team2) {
      setError('Please select two different teams to compare');
      setLoading(false);
      return;
    }
    
    fetchData();
  }, [team1, team2, selectedLeague]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log(`Fetching head to head data for teams ${team1} vs ${team2} in league ${selectedLeague}`);
      
      if (!team1 || !team2) {
        setError('Select both teams to compare');
        setLoading(false);
        return;
      }

      const fromDate = new Date('2024-09-01');
      const toDate = new Date();
      toDate.setDate(toDate.getDate() - 1);
      const response = await headToHeadManager.fetchHeadToHeadMatches(team1, team2, fromDate, toDate);
      console.log('Head to head response:', response);
      
      if (response.status === 'success' && response.data) {
        if (!response.data.response || !Array.isArray(response.data.response) || response.data.response.length === 0) {
          setError('No match data available between selected teams');
          setData(null);
        } else {
          console.log('Number of matches found:', response.data.response.length);
          setData(response.data);
          
          // Process the match data and send it to the parent component
          const matches = response.data.response;
          const currentDate = new Date();
          currentDate.setHours(0, 0, 0, 0);
          
          const sortedMatches = matches
            .filter(match => new Date(match.fixture.date) <= currentDate)
            .sort((a, b) => new Date(b.fixture.date) - new Date(a.fixture.date));
          
          // Pass the sorted matches up to the parent component if the callback exists
          if (onH2hDataReceived && typeof onH2hDataReceived === 'function') {
            onH2hDataReceived(sortedMatches);
          }
        }
      } else {
        setError('Error loading data: ' + (response.message || 'Unknown error'));
        setData(null);
      }
    } catch (err) {
      console.error('Error fetching head to head data:', err);
      setError('Error loading data: ' + (err.message || 'Unknown error'));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (matches, team1Id, team2Id) => {
    const totalMatches = matches.length;
    const team1Wins = matches.filter(match => 
      (match.teams.home.id === team1Id && match.goals.home > match.goals.away) ||
      (match.teams.away.id === team1Id && match.goals.away > match.goals.home)
    ).length;
    const team2Wins = matches.filter(match => 
      (match.teams.home.id === team2Id && match.goals.home > match.goals.away) ||
      (match.teams.away.id === team2Id && match.goals.away > match.goals.home)
    ).length;
    const draws = totalMatches - team1Wins - team2Wins;

    return {
      team1Wins,
      team2Wins,
      draws,
      team1WinPercent: (team1Wins / totalMatches) * 100,
      team2WinPercent: (team2Wins / totalMatches) * 100,
      drawsPercent: (draws / totalMatches) * 100
    };
  };

  const renderTeamWithLastMatch = (team, lastMatch, isHomeTeam) => {
    const getTeamScorers = (events) => {
      if (!events) return [];
      return events
        .filter(event => event.type === "Goal" && event.team.id === team?.id)
        .map(event => event.player.name);
    };

    const scorers = lastMatch?.events ? getTeamScorers(lastMatch.events) : [];
    const scorersText = scorers.length > 0 ? scorers.join(', ') : '';

    const teamInMatch = isHomeTeam ? lastMatch?.teams.home : lastMatch?.teams.away;
    const teamLogo = teamInMatch?.logo || '';

    return (
      <div className="h2h-team">
        <div className="h2h-team-content">
          <img
            src={teamLogo}
            alt={team?.name}
            className="h2h-team-logo"
          />
        </div>
        <div className="h2h-team-name" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          minHeight: '40px',
          wordWrap: 'break-word',
          width: '100%'
        }}>
          {team?.name && team.name.split(' ').map((word, index) => (
            <span key={index} style={{display: 'block', width: '100%'}}>
              {word}
            </span>
          ))}
        </div>
        {scorersText && (
          <div className="h2h-last-match">
            <div className="h2h-last-match-scorers">{scorersText}</div>
          </div>
        )}
      </div>
    );
  };

  const renderH2HContent = () => {
    if (!data || !data.response || data.response.length === 0) {
      return <div className="empty-data">No match data available between selected teams</div>;
    }

    const matches = data.response;
    let team1Wins = 0;
    let team2Wins = 0;
    let draws = 0;
    let totalGoalsTeam1 = 0;
    let totalGoalsTeam2 = 0;
    let lastFiveResultsTeam1 = [];
    let lastFiveResultsTeam2 = [];
    
    const currentDate = new Date();
currentDate.setHours(0, 0, 0, 0);

const sortedMatches = matches
  .filter(match => new Date(match.fixture.date) <= currentDate)
  .sort((a, b) => new Date(b.fixture.date) - new Date(a.fixture.date));
    
    sortedMatches.forEach(match => {
      let team1Goals = 0;
      let team2Goals = 0;
      let resultTeam1 = '';
      let resultTeam2 = '';
      
      if (match.teams.home.id === team1) {
        team1Goals = match.goals.home;
        team2Goals = match.goals.away;
        totalGoalsTeam1 += team1Goals;
        totalGoalsTeam2 += team2Goals;
        
        if (team1Goals > team2Goals) {
          team1Wins++;
          resultTeam1 = 'W';
          resultTeam2 = 'L';
        } else if (team1Goals < team2Goals) {
          team2Wins++;
          resultTeam1 = 'L';
          resultTeam2 = 'W';
        } else {
          draws++;
          resultTeam1 = 'D';
          resultTeam2 = 'D';
        }
      } else {
        team1Goals = match.goals.away;
        team2Goals = match.goals.home;
        totalGoalsTeam1 += team1Goals;
        totalGoalsTeam2 += team2Goals;
        
        if (team1Goals > team2Goals) {
          team1Wins++;
          resultTeam1 = 'W';
          resultTeam2 = 'L';
        } else if (team1Goals < team2Goals) {
          team2Wins++;
          resultTeam1 = 'L';
          resultTeam2 = 'W';
        } else {
          draws++;
          resultTeam1 = 'D';
          resultTeam2 = 'D';
        }
      }
      
      if (lastFiveResultsTeam1.length < 5) {
        lastFiveResultsTeam1.push(resultTeam1);
        lastFiveResultsTeam2.push(resultTeam2);
      }
    });

    const totalMatches = team1Wins + team2Wins + draws;
    const team1WinPercentage = ((team1Wins / totalMatches) * 100).toFixed(1);
    const team2WinPercentage = ((team2Wins / totalMatches) * 100).toFixed(1);
    const drawPercentage = ((draws / totalMatches) * 100).toFixed(1);

    const lastMatch = sortedMatches[0];
    const matchDate = lastMatch ? new Date(lastMatch.fixture.date).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    }) : '';
    const score = lastMatch ? `${lastMatch.goals.home} - ${lastMatch.goals.away}` : '';

    const stats = calculateStats(sortedMatches, team1, team2);

    return (
      <div className="h2h-container">
        <div className="h2h-header">
          {renderTeamWithLastMatch(sortedMatches[0].teams.home, lastMatch, true)}
          <div className="h2h-vs">
            <div className="h2h-vs-label" style={{
              backgroundColor: '#3498db',
              color: 'white',
              padding: '6px 10px',
              borderRadius: '4px',
              fontSize: '13px',
              fontWeight: 'bold',
              marginBottom: '5px',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontFamily: 'Arial, sans-serif',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              border: '1px solid rgb(111, 180, 227)'
            }}>LAST MATCH</div>
            <div className="h2h-vs-date" style={{
              fontSize: '13px',
              color: '#3498db',
              marginBottom: '5px',
              fontFamily: 'Arial, sans-serif',
              fontWeight: '600'
            }}>{matchDate}</div>
            <div style={{
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#7f8c8d',
              margin: '5px 0',
              fontFamily: 'Arial, sans-serif',
              letterSpacing: '1px'
            }}>VS</div>
            <div className="h2h-vs-score" style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#ffffff',
              margin: '5px 0',
              fontFamily: 'Arial, sans-serif',
              textShadow: '0 1px 2px rgba(0,0,0,0.2)'
            }}>{score}</div>
            
            {/* Last Match League Info */}
            {lastMatch?.league && (
              <div className="h2h-vs-league-info" style={{
                marginTop: '10px',
                backgroundColor: 'rgba(147, 207, 247, 0.15)',
                borderRadius: '6px',
                padding: '10px',
                fontSize: '12px',
                boxShadow: '0 2px 4px rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(52, 152, 219, 0.3)'
              }}>
                <div style={{
                  fontWeight: 'bold',
                  marginBottom: '6px',
                  color: '#3498db',
                  fontSize: '14px',
                  textAlign: 'center',
                  textShadow: '0 1px 0 rgba(255,255,255,0.8)',
                  fontFamily: 'Arial, sans-serif'
                }}>
                  {lastMatch.league.name}
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '10px',
                  flexWrap: 'wrap'
                }}>
                  {lastMatch.league.season && (
                    <span style={{
                      backgroundColor: 'rgba(216, 228, 248, 0.3)',
                      padding: '3px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: '#2c3e50',
                      fontWeight: '600',
                      border: '1px solid rgba(52, 152, 219, 0.4)',
                      fontFamily: 'Arial, sans-serif',
                      letterSpacing: '0.5px'
                    }}>
                      Season {lastMatch.league.season}
                    </span>
                  )}
                  {lastMatch.league.round && (
                    <span style={{
                      backgroundColor: 'rgba(216, 228, 248, 0.3)',
                      padding: '3px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: '#2c3e50',
                      fontWeight: '600',
                      border: '1px solid rgba(52, 152, 219, 0.4)',
                      fontFamily: 'Arial, sans-serif',
                      letterSpacing: '0.5px'
                    }}>
                      {lastMatch.league.round}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
          {renderTeamWithLastMatch(sortedMatches[0].teams.away, lastMatch, false)}
        </div>

        {}
        <div className="h2h-total-results">
          <div className="h2h-total-results-title">TOTAL RESULTS</div>
          <div className="h2h-total-matches">{totalMatches} Matches</div>
          <div className="h2h-matches-breakdown">
            <div><span className="wins-team1">{team1Wins}</span> wins <span className="percentage">({team1WinPercentage}%)</span></div>
            <div><span className="wins-draw">{draws}</span> draws <span className="percentage">({drawPercentage}%)</span></div>
            <div><span className="wins-team2">{team2Wins}</span> wins <span className="percentage">({team2WinPercentage}%)</span></div>
          </div>
        </div>
        <div className="h2h-stats-bar">
          <div 
            className="h2h-stats-segment h2h-stats-team1" 
            style={{ width: `${stats.team1WinPercent}%` }}
          />
          <div 
            className="h2h-stats-segment h2h-stats-draw" 
            style={{ width: `${stats.drawsPercent}%`, left: `${stats.team1WinPercent}%` }}
          />
          <div 
            className="h2h-stats-segment h2h-stats-team2" 
            style={{ width: `${stats.team2WinPercent}%` }}
          />
        </div>
        <div className="h2h-stats-logos">
          <img src={lastMatch?.teams.home.logo} alt={sortedMatches[0].teams.home?.name} className="h2h-stats-team-logo" />
          <img src={lastMatch?.teams.away.logo} alt={sortedMatches[0].teams.away?.name} className="h2h-stats-team-logo" />
        </div>

        <div className="h2h-stats">
          <div className="h2h-stat-card">
            <h3 className="h2h-stat-title">Match History</h3>
            <div className="h2h-matches">
              {sortedMatches.slice(0, 5).map((match, index) => (
                <div key={index} className="h2h-match">
                  <div className="h2h-match-teams">
                    <div className="h2h-team-small">
                      <img src={match.teams.home.logo} alt={match.teams.home.name} className="h2h-team-small-logo" />
                      <span className="h2h-team-small-name" style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center'
                      }}>
                        {match.teams.home.name.split(' ').map((word, index) => (
                          <span key={index} style={{display: 'block'}}>
                            {word}
                          </span>
                        ))}
                      </span>
                    </div>
                    <div className="h2h-score-container">
                      <div className="h2h-score">{match.goals.home} - {match.goals.away}</div>
                      <div className="h2h-match-date">{new Date(match.fixture.date).toLocaleDateString()}</div>
                      <div className="h2h-match-info" style={{ 
                        fontSize: '12px', 
                        color: '#555', 
                        marginTop: '8px',
                        textAlign: 'center',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        padding: '10px',
                        maxWidth: '95%',
                        margin: '8px auto 0',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.08)',
                        border: '1px solid #e9ecef',
                        fontFamily: 'Arial, sans-serif'
                      }}>
                        {match.league?.name && (
                          <div style={{ 
                            fontWeight: 'bold', 
                            color: '#3498db',
                            marginBottom: '6px',
                            fontSize: '13px',
                            lineHeight: '1.4',
                            maxWidth: '100%',
                            wordBreak: 'break-word',
                            display: '-webkit-box',
                            WebkitLineClamp: '3',
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            minHeight: '20px',
                            textShadow: '0 1px 0 rgba(255,255,255,0.9)',
                            fontFamily: 'Arial, sans-serif'
                          }}>
                            {match.league.name}
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                          {match.league?.season && (
                            <span style={{ 
                              backgroundColor: '#e9ecef', 
                              padding: '3px 8px', 
                              borderRadius: '4px',
                              fontSize: '11px',
                              color: '#495057',
                              fontWeight: 'bold',
                              border: '1px solid #dee2e6'
                            }}>
                              Season {match.league.season}
                            </span>
                          )}
                          {match.league?.round && (
                            <span style={{ 
                              backgroundColor: '#e9ecef', 
                              padding: '3px 8px', 
                              borderRadius: '4px',
                              fontSize: '11px',
                              color: '#495057',
                              fontWeight: 'bold',
                              border: '1px solid #dee2e6'
                            }}>
                              {match.league.round}
                            </span>
                          )}
                        </div>
                        {match.fixture?.id && (
                          <div style={{
                            marginTop: '6px', 
                            borderTop: '1px dashed #dee2e6',
                            paddingTop: '6px',
                            color: '#6c757d',
                            fontSize: '11px',
                            fontFamily: 'Arial, sans-serif'
                          }}>
                            <span style={{
                              fontWeight: 'bold',
                              color: '#3498db'
                            }}>Fixture ID:</span> {match.fixture.id}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="h2h-team-small">
                      <img src={match.teams.away.logo} alt={match.teams.away.name} className="h2h-team-small-logo" />
                      <span className="h2h-team-small-name" style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center'
                      }}>
                        {match.teams.away.name.split(' ').map((word, index) => (
                          <span key={index} style={{display: 'block'}}>
                            {word}
                          </span>
                        ))}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h2h-page">
      <h2 className="h2h-title" style={{ marginTop: '40px', textAlign: 'center' }}>Head to Head Analysis</h2>
      
      <div className="h2h-teams-display" style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', margin: '20px 0' }}>
        <div className="h2h-team-display" style={{ textAlign: 'center' }}>
          {homeTeamLogo && <img src={homeTeamLogo} alt={homeTeamName || 'Home Team'} style={{ width: '60px', height: '60px', borderRadius: '50%' }} />}
          <div style={{ 
            fontWeight: 'bold', 
            marginTop: '10px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            {homeTeamName && homeTeamName.split(' ').map((word, index) => (
              <span key={index} style={{display: 'block'}}>
                {word}
              </span>
            ))}
          </div>
        </div>
        <div style={{ fontSize: '24px', fontWeight: 'bold' }}>VS</div>
        <div className="h2h-team-display" style={{ textAlign: 'center' }}>
          {awayTeamLogo && <img src={awayTeamLogo} alt={awayTeamName || 'Away Team'} style={{ width: '60px', height: '60px', borderRadius: '50%' }} />}
          <div style={{ 
            fontWeight: 'bold', 
            marginTop: '10px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            {awayTeamName && awayTeamName.split(' ').map((word, index) => (
              <span key={index} style={{display: 'block'}}>
                {word}
              </span>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading head-to-head data...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        renderH2HContent()
      )}
    </div>
  );
};

export default HeadToHeadPage;